'use client'

import React, { useMemo, useState } from 'react'
import Link from 'next/link'
import { FaApple, FaArrowRight, FaArrowLeft, FaRedo } from 'react-icons/fa'
import Breadcrumbs from '@/app/components/Breadcrumbs'
import AppStoreLink from '@/app/components/AppStoreLink'
import Confetti from '@/app/components/Confetti'
import { track } from '@/utils/analytics'

// ─────────────────────────────────────────────────────────────────────────────
// Quiz architecture:
//
//   8 questions × up to 4 options each.
//   Each option has a `score` (0–10) and a list of `tags` that drive the
//   personalised-swap recommendation engine. Higher score = more exposure.
//
//   Max possible score = 8 × 10 = 80.
//   Bands:
//     0–25  → Low
//     26–50 → Medium
//     51–80 → High
//
//   After the user finishes, we pick the 3 highest-scoring answers and look up
//   the matching swap from the SWAPS map. That's the personalised output.
//
// Every question is independent — no branching — so the state machine is just
// a single integer (currentIndex) plus an array of selected option indices.
// No framer-motion: keeps INP tight on mobile.
// ─────────────────────────────────────────────────────────────────────────────

interface QuizOption {
  label: string
  /** 0 = lowest exposure, 10 = highest. */
  score: number
  /** Tags map to entries in SWAPS for personalised recommendations. */
  tags: string[]
}

interface Question {
  id: string
  /** Short heading shown above the options. */
  prompt: string
  /** Optional supporting sub-text. */
  sub?: string
  options: QuizOption[]
}

const QUESTIONS: Question[] = [
  {
    id: 'water',
    prompt: 'What do you drink most days?',
    sub: 'Pick whichever is closest to your usual.',
    options: [
      { label: 'Bottled water', score: 9, tags: ['water_bottled'] },
      { label: 'Tap water (unfiltered)', score: 5, tags: ['water_unfiltered'] },
      { label: 'Filtered tap water', score: 1, tags: ['water_filtered'] },
      { label: 'Mostly other drinks (juice, soda, etc.)', score: 6, tags: ['water_other'] },
    ],
  },
  {
    id: 'food_storage',
    prompt: 'What do you store leftovers in?',
    options: [
      { label: 'Plastic containers', score: 8, tags: ['storage_plastic'] },
      { label: 'A mix of plastic + glass', score: 5, tags: ['storage_mixed'] },
      { label: 'Mostly glass / stainless steel', score: 1, tags: [] },
      { label: 'I don\'t really keep leftovers', score: 2, tags: [] },
    ],
  },
  {
    id: 'microwave',
    prompt: 'How do you reheat food?',
    options: [
      { label: 'Microwave in a plastic container or with plastic wrap', score: 10, tags: ['microwave_plastic'] },
      { label: 'Microwave in glass or ceramic', score: 2, tags: [] },
      { label: 'Stovetop / oven mostly', score: 1, tags: [] },
      { label: 'I rarely reheat', score: 2, tags: [] },
    ],
  },
  {
    id: 'cookware',
    prompt: 'What\'s your everyday pan?',
    options: [
      { label: 'Non-stick (Teflon-style), scratched or peeling', score: 10, tags: ['cookware_scratched_nonstick'] },
      { label: 'Non-stick, looks fine', score: 6, tags: ['cookware_nonstick'] },
      { label: 'Stainless steel / cast iron / carbon steel', score: 1, tags: [] },
      { label: 'Ceramic-coated', score: 3, tags: [] },
    ],
  },
  {
    id: 'coffee_tea',
    prompt: 'How do you usually drink coffee or tea?',
    options: [
      { label: 'To-go cups (coffee shop paper cups, lids)', score: 9, tags: ['coffee_paper_cup'] },
      { label: 'Plastic pods (Keurig, Nespresso)', score: 9, tags: ['coffee_pods'] },
      { label: 'Pyramid mesh tea bags', score: 8, tags: ['tea_mesh'] },
      { label: 'French press / espresso / loose leaf in a glass kettle', score: 2, tags: [] },
    ],
  },
  {
    id: 'cosmetics',
    prompt: 'How many personal-care products do you use daily?',
    sub: 'Skincare, makeup, shampoo, deodorant, count each one.',
    options: [
      { label: '8 or more', score: 8, tags: ['cosmetics_heavy'] },
      { label: '4–7', score: 5, tags: ['cosmetics_medium'] },
      { label: '1–3', score: 2, tags: [] },
      { label: 'Almost none', score: 0, tags: [] },
    ],
  },
  {
    id: 'household',
    prompt: 'Does your household include young kids or a pregnancy?',
    sub: 'Higher stakes during these windows.',
    options: [
      { label: 'Yes: pregnant / trying to conceive', score: 4, tags: ['pregnancy'] },
      { label: 'Yes: infant or toddler (under 3)', score: 4, tags: ['baby'] },
      { label: 'Yes: school-age kid', score: 2, tags: ['kid'] },
      { label: 'No', score: 0, tags: [] },
    ],
  },
  {
    id: 'clothing',
    prompt: 'What are most of your everyday clothes made of?',
    options: [
      { label: 'Mostly synthetic (polyester, nylon, acrylic, fleece)', score: 6, tags: ['clothing_synthetic'] },
      { label: 'A mix of natural + synthetic', score: 3, tags: [] },
      { label: 'Mostly natural fibres (cotton, linen, wool)', score: 1, tags: [] },
      { label: 'Not sure', score: 3, tags: [] },
    ],
  },
]

const MAX_SCORE = QUESTIONS.reduce(
  (sum, q) => sum + Math.max(...q.options.map((o) => o.score)),
  0
)

// ─────────────────────────────────────────────────────────────────────────────
// Swap library. Each tag is keyed to one swap. After scoring, we pick the 3
// highest-scoring answers and look up the swap for each chosen tag. Tag with
// no matching swap = silently skipped (this is how 'no kid' / 'natural fibre'
// answers don't generate spurious recommendations).
// ─────────────────────────────────────────────────────────────────────────────
interface Swap {
  title: string
  description: string
  href: string
}

const SWAPS: Record<string, Swap> = {
  water_bottled: {
    title: 'Switch from bottled water to filtered tap',
    description:
      'Bottled water averages ~240,000 plastic particles per litre (PNAS 2024). A counter-top filter cuts most of it for ~$0.05 per litre.',
    href: '/blog/microplastics-water-filters-compared',
  },
  water_unfiltered: {
    title: 'Add a microplastic-rated filter to your tap',
    description:
      'Carbon block, reverse-osmosis, or ceramic filters all reduce microplastic load by 80%+. Comparison and brand picks here.',
    href: '/blog/microplastics-water-filters-compared',
  },
  water_other: {
    title: 'Watch packaging on the drinks you do choose',
    description:
      'Plastic-bottled drinks and aluminium cans (PE/epoxy liners) leach. Glass-bottled or fountain pours from steel taps are much lower-risk.',
    href: '/blog/microplastics-in-soda',
  },
  storage_plastic: {
    title: 'Move leftover storage to glass',
    description:
      'Glass Pyrex / Anchor and stainless steel are essentially zero-leach. Plastic tubs shed microplastics with heat, oil, and time, and especially in the microwave.',
    href: '/blog/best-plastic-free-food-storage',
  },
  storage_mixed: {
    title: 'Reserve plastic for cold-dry storage only',
    description:
      'Plastic is OK for cold, dry, short-term storage. Avoid heat, fat, acid, and reuse. Glass for everything else.',
    href: '/blog/plastic-wrap-ziploc-bags-microplastics',
  },
  microwave_plastic: {
    title: 'Stop microwaving in plastic',
    description:
      'A 2023 study found microwaving plastic releases up to 4.22 million particles per cm² in 3 minutes. Transfer to glass before reheating, single biggest exposure cut for most homes.',
    href: '/blog/microwaving-plastic-containers-microplastics',
  },
  cookware_scratched_nonstick: {
    title: 'Replace scratched non-stick pans',
    description:
      'Scratched PTFE / Teflon leaches PFAS and microplastic flakes into food at high heat. Cast iron, stainless steel, or carbon steel last decades.',
    href: '/blog/best-non-toxic-cookware',
  },
  cookware_nonstick: {
    title: 'Plan your next pan upgrade',
    description:
      'Non-stick pans degrade with use; PFAS coatings shed even when the pan looks fine. Pick a cast iron or stainless steel pan for everyday cooking.',
    href: '/blog/best-non-toxic-cookware',
  },
  coffee_paper_cup: {
    title: 'BYO mug for coffee shop runs',
    description:
      'Paper to-go cups have a PE plastic liner; a 2022 study estimated ~1,500 microplastic particles per hot drink. Stainless steel travel mugs eliminate it.',
    href: '/blog/microplastics-in-coffee-cups',
  },
  coffee_pods: {
    title: 'Switch from plastic pods to a reusable filter',
    description:
      'Single-cup pods push near-boiling water through plastic, and older machines shed more. French press, a metal-filter pour-over, or refillable steel pods are the lower-contact brews.',
    href: '/blog/microplastics-in-coffee-by-brewing-method',
  },
  tea_mesh: {
    title: 'Switch from mesh tea bags to loose leaf',
    description:
      'Pyramid mesh bags are nylon or PET, a McGill study found billions of particles per cup at brewing temperature. Loose leaf + stainless infuser is the cleanest swap.',
    href: '/blog/microplastics-in-tea-bags',
  },
  cosmetics_heavy: {
    title: 'Audit your skincare and makeup ingredients',
    description:
      '87% of cosmetics contain at least one microplastic polymer. Paste your most-used product\'s ingredient list into the Ingredient Checker to see what\'s flagged.',
    href: '/microplastic-ingredient-checker',
  },
  cosmetics_medium: {
    title: 'Spot-check your daily routine',
    description:
      'Polyethylene, PEG, acrylates, carbomer, dimethicone, these show up in moisturisers, sunscreens, mascara. Paste any ingredient list into the Ingredient Checker.',
    href: '/microplastic-ingredient-checker',
  },
  pregnancy: {
    title: 'Build a pregnancy-window swap list',
    description:
      'The 3-6 months before and during pregnancy is the highest-leverage exposure window. Prenatal vitamins, stretch creams, and food storage matter most.',
    href: '/microplastics-pregnancy',
  },
  baby: {
    title: 'Audit baby bottles + food pouches',
    description:
      'Plastic baby bottles release 1-16 million particles per litre (Nature Food 2020). Glass / silicone / PPSU bottles eliminate it.',
    href: '/blog/best-baby-bottles-microplastic-free',
  },
  kid: {
    title: 'Switch the school lunchbox',
    description:
      'Plastic lunchboxes shed microplastics into kids\' lunches daily. Stainless steel (LunchBots, PlanetBox) and silicone are the cleanest options.',
    href: '/blog/back-to-school-microplastics-guide',
  },
  clothing_synthetic: {
    title: 'Add a microfibre filter to your washing machine',
    description:
      'A single wash releases 700,000+ microfibres from synthetic clothing. Guppyfriend bags and lint filters cut most of it without buying new clothes.',
    href: '/blog/microplastics-in-laundry',
  },
}

interface ResultSummary {
  score: number
  band: 'low' | 'medium' | 'high'
  swaps: Swap[]
}

function computeResult(selections: number[]): ResultSummary {
  let score = 0
  const scoredTags: Array<{ tag: string; score: number }> = []

  selections.forEach((optionIndex, qIndex) => {
    if (optionIndex < 0) return
    const option = QUESTIONS[qIndex].options[optionIndex]
    score += option.score
    for (const tag of option.tags) {
      scoredTags.push({ tag, score: option.score })
    }
  })

  // Pick highest-scoring tags first; bail at 3 distinct swaps so we don't
  // present an overwhelming list. Dedupe by tag.
  const seen = new Set<string>()
  const swaps: Swap[] = []
  scoredTags
    .sort((a, b) => b.score - a.score)
    .forEach(({ tag }) => {
      if (swaps.length >= 3) return
      if (seen.has(tag)) return
      seen.add(tag)
      const swap = SWAPS[tag]
      if (swap) swaps.push(swap)
    })

  const pct = score / MAX_SCORE
  const band: 'low' | 'medium' | 'high' = pct < 0.32 ? 'low' : pct < 0.62 ? 'medium' : 'high'

  return { score, band, swaps }
}

const BAND_COPY: Record<ResultSummary['band'], { title: string; sub: string; tint: string }> = {
  low: {
    title: 'Lower-than-average exposure',
    sub: "Your routine already avoids the biggest sources. A few targeted swaps will push you even lower.",
    tint: 'text-forest',
  },
  medium: {
    title: 'Average-to-elevated exposure',
    sub: "You're hitting several of the highest-leverage exposure sources. Two or three swaps would change your daily load dramatically.",
    tint: 'text-amber-600',
  },
  high: {
    title: 'High exposure load',
    sub: "Your routine includes several high-leverage exposure sources. The good news: that means the first 2 or 3 swaps will deliver the biggest cuts.",
    tint: 'text-red-600',
  },
}

export default function RiskChecker() {
  // -1 = unanswered. Length matches QUESTIONS.length so we can show progress
  // and detect partial completion without sentinel values.
  const [selections, setSelections] = useState<number[]>(() => QUESTIONS.map(() => -1))
  const [currentIndex, setCurrentIndex] = useState(0)
  const [hasStarted, setHasStarted] = useState(false)
  const [showResults, setShowResults] = useState(false)

  const totalQuestions = QUESTIONS.length
  const currentQuestion = QUESTIONS[currentIndex]

  // Memoise the result so we don't recompute on every minor re-render. Only
  // matters once showResults is true.
  const result = useMemo(() => (showResults ? computeResult(selections) : null), [
    showResults,
    selections,
  ])

  const handleAnswer = (optionIndex: number) => {
    if (!hasStarted) {
      setHasStarted(true)
      // Engagement signal: user actually engaged with the quiz vs. bounced.
      track('risk_checker_start', {
        cta_surface: 'risk_checker',
        page_type: 'tool_risk_checker',
      })
    }

    const next = [...selections]
    next[currentIndex] = optionIndex
    setSelections(next)

    // Auto-advance after a short delay so the user sees their choice
    // highlighted before the next question lands. Keeps the flow snappy
    // without feeling abrupt.
    setTimeout(() => {
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex(currentIndex + 1)
      } else {
        // Last question answered — compute + reveal results.
        const summary = computeResult(next)
        setShowResults(true)
        // KEY event. The score + band are both useful breakdowns in GA4.
        track('risk_checker_complete', {
          cta_surface: 'risk_checker',
          page_type: 'tool_risk_checker',
          risk_score: summary.score,
          risk_band: summary.band,
        })
      }
    }, 220)
  }

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleRetake = () => {
    setSelections(QUESTIONS.map(() => -1))
    setCurrentIndex(0)
    setHasStarted(false)
    setShowResults(false)
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="content pt-6">
        <Breadcrumbs items={[{ label: 'Risk Checker' }]} />
      </div>

      {/* HERO — collapses to a short header once the user has started */}
      <section className="content pt-6 pb-6">
        {!hasStarted && !showResults && (
          <>
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
              Free 60-second quiz · No signup
            </p>
            <h1 className="font-display text-[clamp(1.875rem,5vw,3rem)] font-bold text-ink mb-4 leading-[1.08]">
              Microplastic Risk Checker
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Answer 8 quick questions. Get your personalised microplastic exposure score, plus
              the three highest-impact swaps for your routine. Backed by published research
              (PNAS, NEJM, McGill, UNM, Nature Food).
            </p>
          </>
        )}
        {(hasStarted || showResults) && (
          <h1 className="font-display text-2xl md:text-3xl font-bold text-ink mb-2 leading-tight">
            Microplastic Risk Checker
          </h1>
        )}
      </section>

      {/* QUIZ */}
      {!showResults && (
        <section className="content pb-12">
          {/* Progress */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 mb-2">
              <span>
                Question {currentIndex + 1} of {totalQuestions}
              </span>
              <span>{Math.round(((currentIndex + (selections[currentIndex] >= 0 ? 1 : 0)) / totalQuestions) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-ink/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-forest transition-[color,background-color,border-color] duration-300 ease-out"
                style={{
                  width: `${
                    ((currentIndex + (selections[currentIndex] >= 0 ? 1 : 0)) / totalQuestions) * 100
                  }%`,
                }}
              />
            </div>
          </div>

          {/* Question card */}
          <div className="bg-cream rounded-2xl border border-ink/10 p-6 md:p-8">
            <h2 className="font-display text-xl md:text-2xl font-bold text-ink mb-2 leading-snug">
              {currentQuestion.prompt}
            </h2>
            {currentQuestion.sub && (
              <p className="text-sm text-gray-500 mb-5">{currentQuestion.sub}</p>
            )}
            <ul className="space-y-2.5">
              {currentQuestion.options.map((option, optionIndex) => {
                const isSelected = selections[currentIndex] === optionIndex
                return (
                  <li key={optionIndex}>
                    <button
                      type="button"
                      onClick={() => handleAnswer(optionIndex)}
                      aria-pressed={isSelected}
                      className={`w-full text-left px-4 py-3.5 rounded-control border-2 transition-[color,background-color,border-color,text-decoration-color] duration-150 ${
                        isSelected
                          ? 'bg-forest/10 border-forest text-ink font-semibold'
                          : 'bg-white border-ink/10 text-gray-700 hover:border-forest/40 hover:bg-forest/5'
                      } pressable-sm`}
                    >
                      {option.label}
                    </button>
                  </li>
                )
              })}
            </ul>

            {/* Back button — only when there's a previous question to go to */}
            {currentIndex > 0 && (
              <div className="mt-5 pt-4 border-t border-black/5 flex">
                <button
                  type="button"
                  onClick={handleBack}
                  data-analytics-action="quiz_back"
                  className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-forest px-2 py-2"
                >
                  <FaArrowLeft className="w-3 h-3" aria-hidden="true" />
                  Back
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* RESULTS */}
      {showResults && result && (
        <section className="content pb-16" aria-live="polite">
          {/* Score card. Confetti celebrates completing the assessment (not the
              score itself), a gentle reward for finishing, same pattern as the
              waitlist success. Contained + reduced-motion-safe via the component. */}
          <div className="relative overflow-hidden border-t-2 border-ink pt-7 pb-2 mb-8">
            <Confetti count={40} />
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
              Your exposure score
            </p>
            <div className="flex items-baseline gap-2 mb-3">
              <span className="font-display text-6xl md:text-7xl font-bold text-ink tabular-nums">
                {result.score}
              </span>
              <span className="text-2xl text-gray-400 font-medium">/ {MAX_SCORE}</span>
            </div>
            <h2 className={`font-display text-xl md:text-2xl font-bold mb-2 ${BAND_COPY[result.band].tint}`}>
              {BAND_COPY[result.band].title}
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-2xl">{BAND_COPY[result.band].sub}</p>
          </div>

          {/* Personalised swaps */}
          <div className="mb-6">
            <h3 className="font-display text-xl md:text-2xl font-bold text-ink mb-2">
              Your top {result.swaps.length} swap{result.swaps.length === 1 ? '' : 's'}
            </h3>
            {result.swaps.length === 0 ? (
              <div className="border-t border-ink/10 pt-5">
                <p className="text-gray-700 leading-relaxed">
                  No high-impact swaps surfaced from your answers. Your routine already avoids
                  the biggest sources. Browse the{' '}
                  <Link href="/microplastics-guide" className="text-forest underline underline-offset-4 decoration-forest/30 hover:decoration-forest font-medium">
                    full guide
                  </Link>{' '}
                  for fine-tuning.
                </p>
              </div>
            ) : (
              <ol className="border-b border-ink/10">
                {result.swaps.map((swap, i) => (
                  <li
                    key={swap.href}
                    className="border-t border-ink/10 py-5 flex gap-4 sm:gap-6"
                  >
                    <span aria-hidden="true" className="shrink-0 font-mono text-sm text-forest/60 pt-1 tracking-wider">
                      0{i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base md:text-lg font-bold text-ink mb-1">
                        {swap.title}
                      </h4>
                      <p className="text-sm text-gray-600 leading-relaxed mb-3">
                        {swap.description}
                      </p>
                      <Link
                        href={swap.href}
                        className="inline-flex items-center gap-1 text-sm text-forest font-semibold underline underline-offset-4 decoration-forest/30 hover:decoration-forest"
                      >
                        Read the guide
                        <FaArrowRight className="w-3 h-3" aria-hidden="true" />
                      </Link>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>

          {/* App Store CTA — peak motivation moment */}
          <div className="border-t border-ink/10 pt-8 mb-6">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
              Make it real
            </p>
            <h3 className="font-display text-xl md:text-2xl font-bold text-ink mb-2">
              Scan your actual products before you buy them
            </h3>
            <p className="text-gray-600 leading-relaxed mb-5 max-w-2xl">
              The MicroPlastics iOS app gives a 0–100 risk score on any product, barcode plus a
              packaging photo, with the cited research behind every score. Free with 3 scans.
            </p>
            <AppStoreLink
              source="risk_checker_results"
              ctaPosition="tool_results"
              trackingParams={{
                page_type: 'tool_risk_checker',
                risk_band: result.band,
                risk_score: result.score,
              }}
              className="inline-flex items-center gap-2 bg-ink text-cream font-semibold px-6 py-3 min-h-[48px] rounded-control hover:bg-ink/90 transition-colors pressable"
            >
              <FaApple className="w-5 h-5" aria-hidden="true" />
              Download on the App Store
            </AppStoreLink>
          </div>

          {/* Retake */}
          <div className="text-center">
            <button
              type="button"
              onClick={handleRetake}
              data-analytics-action="quiz_restart"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-forest px-3 py-2"
            >
              <FaRedo className="w-3 h-3" aria-hidden="true" />
              Retake the quiz
            </button>
          </div>
        </section>
      )}
    </div>
  )
}
